import React from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CountryFlag from 'react-country-flag';
import lookup from 'country-code-lookup';
import { CUSTOM_FLAGS } from './team-crest';

/* ------------------------------------------------------------------ *
 * BWF · Event list — "Match Card" styling to match the event detail
 * page (Direction 1). Same data, same navigation; restyled rows.
 * ------------------------------------------------------------------ */

const ORANGE = '#ff9800';
const AMBER = '#ffc107';
const GREEN = '#43c463';
const RED = '#ef5350';
const LINE = '1px solid rgba(255,255,255,0.08)';
const LINE2 = '1px solid rgba(255,255,255,0.14)';

const shortCode = (name) =>
  lookup.byCountry(name)?.iso3 || (name || '').slice(0, 3).toUpperCase();

const Row = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  background: theme.palette.background.paper,
  border: LINE,
  borderRadius: 14,
  padding: '13px 15px',
  marginBottom: 10,
  transition: 'border-color .15s ease, background .15s ease, transform .1s ease',
  '&:hover': { borderColor: 'rgba(255,152,0,0.5)', background: '#242424' },
  '&:active': { transform: 'scale(0.995)' },
}));

function FlagCircle({ name, size = 28 }) {
  const cc = lookup.byCountry(name);
  const customFlag = name ? CUSTOM_FLAGS[name] : undefined;
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', overflow: 'hidden', bgcolor: '#222',
      boxShadow: '0 0 0 1.5px rgba(255,255,255,0.15)', flex: '0 0 auto',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {cc
        ? <CountryFlag countryCode={cc.iso2} svg style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : customFlag
          ? <img src={customFlag} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{(name || '').slice(0, 2)}</Typography>}
    </Box>
  );
}

function MiniPill({ status }) {
  const map = {
    upcoming: { label: 'Upcoming', color: '#b0b0b0', dot: '#7a7a7a', live: false },
    live: { label: 'Awaiting result', color: ORANGE, dot: ORANGE, live: true },
    finished: { label: 'Full time', color: '#fff', dot: null, live: false },
  };
  const s = map[status];
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.625, px: 1, py: '3px', borderRadius: 999, flex: '0 0 auto',
      background: s.live ? 'rgba(255,152,0,0.14)' : 'rgba(255,255,255,0.06)',
      border: s.live ? '1px solid rgba(255,152,0,0.4)' : LINE2,
    }}>
      {s.dot && (
        <Box sx={{
          width: 6, height: 6, borderRadius: '50%', background: s.dot,
          animation: s.live ? 'bwfPulse 1.4s ease-in-out infinite' : 'none',
          '@keyframes bwfPulse': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.35, transform: 'scale(0.7)' } },
        }} />
      )}
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: s.color }}>
        {s.label}
      </Typography>
    </Box>
  );
}

function TeamSide({ name, align }) {
  const code = shortCode(name);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
      flexDirection: align === 'right' ? 'row-reverse' : 'row',
    }}>
      <FlagCircle name={name} />
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>{code}</Typography>
    </Box>
  );
}

function BetChip({ status, myBet, hasBet }) {
  // No prediction made
  if (!hasBet) {
    const muted = status !== 'upcoming';
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, px: 1.1, py: '4px', borderRadius: 999,
        color: muted ? '#7a7a7a' : RED,
        background: muted ? 'transparent' : 'rgba(239,83,80,0.12)',
        border: muted ? LINE2 : '1px solid rgba(239,83,80,0.4)',
      }}>
        <WarningAmberIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>No bet</Typography>
      </Box>
    );
  }

  const win = status === 'finished' && myBet.points === 3;
  const pointsKnown = status === 'finished' && myBet.points !== null && myBet.points !== undefined;
  const scoreEl = (
    <Typography component="span" sx={{ fontFamily: '"Archivo", Inter, sans-serif', fontWeight: 800, fontSize: 14, color: '#fff', whiteSpace: 'nowrap' }}>
      {myBet.score1} : {myBet.score2}
    </Typography>
  );

  if (status === 'finished') {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.875 }}>
        {scoreEl}
        {pointsKnown && (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.375, px: 0.875, py: '3px', borderRadius: 999,
            fontSize: 12, fontWeight: 800,
            color: win ? '#1a1207' : '#b0b0b0',
            background: win ? AMBER : 'transparent',
            border: win ? 'none' : LINE2,
          }}>
            {win && <EmojiEventsIcon sx={{ fontSize: 13 }} />}
            {myBet.points} pt{myBet.points === 1 ? '' : 's'}
          </Box>
        )}
      </Box>
    );
  }

  // upcoming / live → show locked pick
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.625 }}>
      <LockIcon sx={{ fontSize: 13, color: GREEN }} />
      <Typography sx={{ fontSize: 11, color: '#b0b0b0', mr: 0.25 }}>Your pick</Typography>
      {scoreEl}
    </Box>
  );
}

function EventCard({ event, onClick }) {
  const format = "yyyy-MM-dd'T'HH:mm:ss'Z'";
  const evtTime = DateTime.fromFormat(event.time, format, { zone: 'utc' }).toLocal();
  const isFuture = evtTime > DateTime.now();
  const hasResult = event.score1 >= 0 && event.score2 >= 0;
  const status = isFuture ? 'upcoming' : (hasResult ? 'finished' : 'live');
  const hasBet = event.my_bet && event.my_bet.score1 !== null && event.my_bet.score2 !== null;

  const center = hasResult ? (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, fontFamily: '"Archivo", Inter, sans-serif' }}>
      <Typography component="span" sx={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{event.score1}</Typography>
      <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, color: '#7a7a7a' }}>:</Typography>
      <Typography component="span" sx={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{event.score2}</Typography>
    </Box>
  ) : status === 'live' ? (
    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#7a7a7a', fontFamily: '"Archivo", Inter, sans-serif', whiteSpace: 'nowrap' }}>– : –</Typography>
  ) : (
    <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#7a7a7a', fontFamily: '"Archivo", Inter, sans-serif', whiteSpace: 'nowrap' }}>VS</Typography>
  );

  return (
    <Row onClick={onClick}>
      {/* matchup + status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
          <TeamSide name={event.team1} align="left" />
          <Box sx={{ textAlign: 'center' }}>{center}</Box>
          <TeamSide name={event.team2} align="right" />
        </Box>
        <MiniPill status={status} />
      </Box>

      {/* date/time + your bet */}
      <Box sx={{ mt: 1.25, pt: 1.25, borderTop: LINE, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#b0b0b0', whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: ORANGE }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{evtTime.toFormat('ccc, dd LLL')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AlarmIcon sx={{ fontSize: 14, color: ORANGE }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{evtTime.toFormat('HH:mm')}</Typography>
          </Box>
        </Box>
        <BetChip status={status} myBet={event.my_bet} hasBet={hasBet} />
      </Box>
    </Row>
  );
}

export default function EventList({ events }) {
  const navigate = useNavigate();
  const navEvents = (events || []).map((e) => ({ id: e.id, team1: e.team1, team2: e.team2 }));
  const openEvent = (eventId) => navigate(`/event/${eventId}`, { state: { navEvents } });

  return (
    <React.Fragment>
      {events && events.map((event) => (
        <EventCard key={event.id} event={event} onClick={() => openEvent(event.id)} />
      ))}
    </React.Fragment>
  );
}
